import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizedPersonDto } from 'src/app/core/data-models/authorized-person.data.models';
import { LanguageService } from 'src/app/shared/services/language.service';
import { saudiNidValidator } from 'src/app/shared/shared/jari-validators';
import { Calendar as PrimeCalendar } from 'primeng/calendar';

function fourWordsValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').toString().trim();
  if (!value) return null;
  const parts = value.split(/\s+/).filter((p: string) => p.length > 0);
  return parts.length === 4 ? null : { fourWords: true };
}

function minAge18Validator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (raw === null || raw === undefined || raw === '') return null;

  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) return { invalidDate: true };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dob = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  if (dob.getTime() > today.getTime()) return { future: true };

  const eighteenth = new Date(dob);
  eighteenth.setFullYear(eighteenth.getFullYear() + 18);
  if (eighteenth.getTime() > today.getTime()) return { minAge: true };

  return null;
}

@Component({
  selector: 'app-applicant-identity',
  templateUrl: './applicant-identity.component.html',
  styleUrls: ['./applicant-identity.component.css'],
})
export class ApplicantIdentityComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isDetails = false;
  /** Optional seed from fund first-step endpoint. */
  @Input() formData?: Partial<AuthorizedPersonDto> | null;

  /** Locked profile fields remain editable except designation once API returns Arabic name + national ID. */
  private readonly prefilledExceptDesignationControls = ['arabicFullName', 'englishFullName', 'nationalId', 'dob', 'mobile'] as const;

  form!: FormGroup;
  dateType: 'hijri' | 'gregorian' = 'hijri';
  allowDateTypeSelection = true;
  gregorianDateModel: Date | null = null;

  /** Match register personal-info Gregorian navigator range (18+). */
  readonly maxBirthDate: Date = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d;
  })();
  readonly dobYearRange = `${new Date().getFullYear() - 120}:${new Date().getFullYear() - 18}`;

  designations: { label: string; value: string }[] = [];

  private langSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    public languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      arabicFullName: ['', [Validators.required, fourWordsValidator]],
      englishFullName: [''],
      nationalId: ['', [Validators.required, saudiNidValidator]],
      dob: ['', [Validators.required, minAge18Validator]],
      mobile: ['', [Validators.required, Validators.pattern(/^5\d{8}$/)]],
      firstName: [''],
      lastName: [''],
      designation: ['', Validators.required],
    });
    if (this.isDetails) {
      this.form.disable();
    }
    this.refreshDesignationOptions();
    this.langSub = this.translate.onLangChange.subscribe(() => this.refreshDesignationOptions());
    this.patchFormFromFundData(this.formData);
    this.applyIdentityFieldLocksFromSeed(this.formData);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) return;
    if (changes['formData']) {
      const next = changes['formData'].currentValue as Partial<AuthorizedPersonDto> | null | undefined;
      this.patchFormFromFundData(next);
      this.applyIdentityFieldLocksFromSeed(next);
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private refreshDesignationOptions(): void {
    this.designations = [
      { label: this.translate.instant('fundApplication.positionCeo'), value: 'ceo' },
      { label: this.translate.instant('fundApplication.positionBoardMember'), value: 'board' },
      { label: this.translate.instant('fundApplication.positionOther'), value: 'other' },
    ];
  }

  /** True while identity fields except designation stay disabled (fund step edit after saved draft). */
  get identityLockedExceptDesignation(): boolean {
    return this.shouldLockExceptDesignation(this.formData);
  }

  private shouldLockExceptDesignation(p?: Partial<AuthorizedPersonDto> | null): boolean {
    if (this.isDetails) return true;
    if (!p) return false;
    const name = String((p.arabicFullName as string | undefined)?.trim?.() ?? p.arabicFullName ?? '').trim();
    const nid = String((p.nationalId as string | undefined)?.trim?.() ?? p.nationalId ?? '').replace(/\D/g, '');
    if (nid.length !== 10) return false;
    const hasName = !!name;
    const hasDob = !!this.normalizeDobIso(p.dob);
    /** Lock identity when NID matches profile and Arabic name or DOB came from backend. */
    return hasName || hasDob;
  }

  private applyIdentityFieldLocksFromSeed(seed?: Partial<AuthorizedPersonDto> | null): void {
    if (!this.form || this.isDetails) return;
    const lock = this.shouldLockExceptDesignation(seed);
    for (const key of this.prefilledExceptDesignationControls) {
      const c = this.form.get(key);
      if (!c) continue;
      if (lock) c.disable({ emitEvent: false });
      else c.enable({ emitEvent: false });
    }
    this.form.get('designation')?.enable({ emitEvent: false });
  }

  private patchFormFromFundData(p?: Partial<AuthorizedPersonDto> | null): void {
    if (!p) {
      this.allowDateTypeSelection = true;
      this.dateType = 'hijri';
      this.syncGregorianModelFromControl();
      return;
    }
    const dob = this.normalizeDobIso(p.dob);
    const arabicFullName = (p.arabicFullName || '').trim();
    const first = (p.firstName || '').trim() || this.splitFirstFromFullName(arabicFullName);
    const last = (p.lastName || '').trim() || this.splitLastFromFullName(arabicFullName);
    const normalizedNid = this.normalizeNationalId(p.nationalId || '');
    const normalizedMobile = this.normalizeSaudiLocalPhone(p.mobile || '');
    this.form.patchValue({
      arabicFullName,
      englishFullName: p.englishFullName || '',
      nationalId: normalizedNid,
      dob,
      mobile: normalizedMobile,
      firstName: first,
      lastName: last,
      designation: p.designation || '',
    });

    this.dateType = this.resolveDateType(p.dobCalendarType, dob, normalizedNid);
    this.allowDateTypeSelection = this.isDetails
      ? false
      : !dob || !this.shouldLockExceptDesignation(p);
    this.syncGregorianModelFromControl();
  }

  private splitFirstFromFullName(full: string | undefined): string {
    if (!full?.trim()) return '';
    const parts = full.trim().split(/\s+/);
    return parts[0] || '';
  }

  private splitLastFromFullName(full: string | undefined): string {
    if (!full?.trim()) return '';
    const parts = full.trim().split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  setDateType(mode: 'hijri' | 'gregorian'): void {
    if (this.isDetails || this.identityLockedExceptDesignation || this.dateType === mode || !this.allowDateTypeSelection) return;
    this.dateType = mode;
    this.form.get('dob')?.setValue('');
    this.form.get('dob')?.markAsTouched();
    this.gregorianDateModel = null;
  }

  onGregorianPickerChange(value: Date | null): void {
    if (this.isDetails || this.identityLockedExceptDesignation || this.form.get('dob')?.disabled) return;
    this.gregorianDateModel = value ?? null;
    const iso = value ? this.formatGregorianIso(value) : '';
    this.form.get('dob')?.setValue(iso);
    this.form.get('dob')?.markAsTouched();
  }

  onGregorianCalendarShow(calendar: PrimeCalendar): void {
    if (this.gregorianDateModel) return;
    const view = this.maxBirthDate;
    calendar.currentMonth = view.getMonth();
    calendar.currentYear = view.getFullYear();
    calendar.createMonths(calendar.currentMonth, calendar.currentYear);
  }

  onNationalIdInput(): void {
    const control = this.form.get('nationalId');
    if (!control) return;
    const normalized = this.normalizeNationalId(control.value);
    control.setValue(normalized, { emitEvent: false });
    if (this.allowDateTypeSelection && !this.form.get('dob')?.value) {
      const first = normalized.charAt(0);
      if (first === '1') this.dateType = 'hijri';
      if (first === '2') this.dateType = 'gregorian';
    }
  }

  onMobileInput(): void {
    const control = this.form.get('mobile');
    if (!control) return;
    const normalized = this.normalizeSaudiLocalPhone(control.value);
    control.setValue(normalized, { emitEvent: false });
  }

  private syncGregorianModelFromControl(): void {
    const raw = (this.form.get('dob')?.value || '').toString();
    if (!raw) {
      this.gregorianDateModel = null;
      return;
    }
    const parsed = new Date(raw);
    this.gregorianDateModel = isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatGregorianIso(d: Date): string {
    const yyyy = d.getFullYear().toString().padStart(4, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd = d.getDate().toString().padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private normalizeDobIso(value: unknown): string {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) return this.formatGregorianIso(parsed);
    return raw;
  }

  private resolveDateType(
    hint: unknown,
    dob: string,
    nationalId: string
  ): 'hijri' | 'gregorian' {
    if (hint === 'hijri' || hint === 'gregorian') return hint;
    if (nationalId.startsWith('1')) return 'hijri';
    if (nationalId.startsWith('2')) return 'gregorian';
    if (!dob) return 'hijri';

    const yyyy = parseInt((dob.split('-')[0] || '').trim(), 10);
    if (!isNaN(yyyy)) return yyyy >= 1700 ? 'gregorian' : 'hijri';

    return 'hijri';
  }

  private normalizeNationalId(value: unknown): string {
    return (value ?? '')
      .toString()
      .replace(/\D/g, '')
      .slice(0, 10);
  }

  private normalizeSaudiLocalPhone(value: unknown): string {
    let digits = (value ?? '').toString().replace(/\D/g, '');
    if (!digits) return '';

    if (digits.startsWith('00966')) digits = digits.slice(5);
    else if (digits.startsWith('966')) digits = digits.slice(3);

    if (digits.startsWith('05') && digits.length >= 10) digits = digits.slice(1);
    if (digits.length > 9) digits = digits.slice(0, 9);

    return digits;
  }

  /** Identity + designation only; compliance fields merged in `ApplyfundComponent`. */
  getSubmitPayload(): Partial<AuthorizedPersonDto> {
    const v = this.form.getRawValue();
    const dobStr = (v.dob as string)?.trim() ?? '';
    return {
      designation: v.designation,
      dob: dobStr,
      dobCalendarType: this.dateType,
      nationalId: (v.nationalId as string)?.trim() ?? '',
      firstName: (v.firstName as string)?.trim() ?? '',
      lastName: (v.lastName as string)?.trim() ?? '',
      arabicFullName: (v.arabicFullName as string)?.trim() ?? '',
      englishFullName: (v.englishFullName as string)?.trim() ?? '',
      mobile: (v.mobile as string)?.trim() ?? '',
    };
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  /** Review / details: show DOB as text (no Hijri–Gregorian switch or pickers). */
  get formattedDobReadOnly(): string {
    const raw = (this.form?.get('dob')?.value ?? '').toString().trim();
    if (!raw) {
      return '—';
    }
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      const loc = this.languageService.currentLanguage === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-GB';
      try {
        return d.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' });
      } catch {
        return raw;
      }
    }
    return raw;
  }
}
